import { Link, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { apiService, eventService } from "../api/client";

interface PathItem {
    pathText: string;
    route: string | null;
    displayText: string | null;
    tableName: string | null;
    tableId: string | null;
    pathFunction: string | null;
    pathDomain: string | null;
    breadcrumb: {
        displayLabel: string;
        hyperlink: string;
    } | null;
}

const Breadcrumbs = () => {
    const location = useLocation();
    const pathnames = useMemo(
        () => location.pathname.split("/").filter((x) => x),
        [location.pathname]
    );
    const [paths, setPaths] = useState<PathItem[]>([]);
    const theme = useTheme();

    // Create initial paths array
    const createPaths = useCallback((): PathItem[] => {
        return pathnames.map((value, index) => {
            const pathText = `/${pathnames.slice(0, index + 1).join("/")}`;
            let route: string | null = null;
            let displayText: string | null = null;
            let tableName: string | null = null;
            let pathFunction: string | null = null;
            let pathDomain: string | null = null;

            switch (pathText) {
                case "/events":
                    route = "/events";
                    displayText = "events";
                    break;
                case "/venues":
                    route = "/venues";
                    displayText = "venues";
                    break;
                case "/events/new":
                    route = "/events";
                    displayText = "New Event";
                    break;
                case "/venues/new":
                    route = "/venues";
                    displayText = "New Venue";
                    break;
                case "/event-map":
                    route = "/events";
                    displayText = "events";
                    tableName = "events";
                    pathFunction = "Map";
                    pathDomain = "Event";
                    break;
                case "/venue-map":
                    route = "/venues";
                    displayText = "venues";
                    tableName = "venues";
                    pathFunction = "Map";
                    pathDomain = "Venue";
                    break;
                case "/device-report":
                case "/device-engineering":
                    route = "/devices";
                    displayText = "devices";
                    tableName = "instances";
                    pathFunction = pathText.includes("report") ? "Report" : "Engineering";
                    pathDomain = "Device";
                    break;
                case "/product-report":
                case "/product-engineering":
                    route = "/devices";
                    displayText = "devices";
                    tableName = "templates";
                    pathFunction = pathText.includes("report") ? "Report" : "Engineering";
                    pathDomain = "Product";
                    break;
                case "/category-report":
                case "/category-engineering":
                    route = "/devices";
                    displayText = "devices";
                    tableName = "categories";
                    pathFunction = pathText.includes("report") ? "Report" : "Engineering";
                    pathDomain = "Category";
                    break;
                case "/zone-report":
                    route = "/events";
                    displayText = "events";
                    tableName = "zones";
                    pathFunction = "Report";
                    pathDomain = "Zone";
                    break;
                case "/event-report":
                    route = "/events";
                    displayText = "events";
                    tableName = "events";
                    pathFunction = "Report";
                    pathDomain = "Event";
                    break;
                case "/venue-report":
                    route = "/venues";
                    displayText = "venues";
                    tableName = "venues";
                    pathFunction = "Report";
                    pathDomain = "Venue";
                    break;
            }

            const tableId = !isNaN(Number(value)) ? value : null;

            let breadcrumb = null;
            if (tableId === null) {
                // Case (a)
                breadcrumb = {
                    displayLabel: displayText ? displayText.toUpperCase() : value.toUpperCase(),
                    hyperlink: route || pathText
                };
            } else {
                // Case (b) - initial values, will be updated by useEffect
                breadcrumb = {
                    displayLabel: tableId,
                    hyperlink: ""
                };
            }

            return {
                pathText,
                route,
                displayText,
                tableName,
                tableId,
                pathFunction,
                pathDomain,
                breadcrumb
            };
        });
    }, [pathnames]);

    // Initialize paths on location change
    useEffect(() => {
        setPaths(createPaths());
    }, [createPaths, location.pathname]);

    // Update paths with database lookups
    useEffect(() => {
        let cancelled = false;

        const updatePathsWithDbData = async () => {
            const updatedPaths = [...paths];
            const lookupCache = new Map<string, Promise<any>>();

            // Only run if there is at least one ID segment whose breadcrumb
            // is still a placeholder (empty hyperlink).
            const needsUpdate = updatedPaths.some((path, index) => {
                if (path.tableId === null || (path.breadcrumb && path.breadcrumb.hyperlink !== "")) {
                    return false;
                }

                const parent = index > 0 ? updatedPaths[index - 1] : null;
                return Boolean(
                    parent?.tableName &&
                    parent.route &&
                    parent.pathFunction &&
                    parent.pathDomain
                );
            });
            if (!needsUpdate) {
                return;
            }

            for (let i = 0; i < updatedPaths.length; i++) {
                if (cancelled) {
                    return;
                }

                const path = updatedPaths[i];
                if (path.tableId !== null) {
                    // Get tablename, route, and pathFunction from previous path
                    const prevPath = i > 0 ? updatedPaths[i - 1] : null;
                    const tableName = prevPath?.tableName;
                    const route = prevPath?.route;
                    const pathFunction = prevPath?.pathFunction;
                    const pathDomain = prevPath?.pathDomain;

                    if (tableName && route && pathFunction && pathDomain) {
                        try {
                            const lookupKey = `${tableName}:${path.tableId}`;
                            let lookupPromise = lookupCache.get(lookupKey);
                            if (!lookupPromise) {
                                const id = Number(path.tableId);
                                lookupPromise = tableName === "events"
                                    ? eventService.getEventWithVenue(id)
                                    : apiService.get(tableName, id);
                                lookupCache.set(lookupKey, lookupPromise);
                            }
                            const result = await lookupPromise;

                            if (cancelled) {
                                return;
                            }

                            path.breadcrumb = {
                                displayLabel: `${pathDomain} ${pathFunction} for ${result.name || path.tableId}`,
                                hyperlink: path.pathText,
                            };
                        } catch (error) {
                            console.error("Error fetching breadcrumb data:", error);
                        }
                    }
                }
            }

            if (!cancelled) {
                setPaths(updatedPaths);
            }
        };

        if (paths.length > 0) {
            updatePathsWithDbData();
        }

        return () => {
            cancelled = true;
        };
    }, [paths, location.pathname]);

    const renderBreadcrumbs = () => {
        // Hide breadcrumbs on the Home page
        if (location.pathname === "/") {
            return null;
        }
        const linkColor = theme.palette.mode === "dark"
            ? theme.palette.primary.light
            : theme.palette.primary.dark;

        return (
            <nav style={{ margin: "5px 15px 5px 15px", fontSize: "18px", position: "relative", zIndex: 0 }}>
                <Link to="/" style={{ textDecoration: "none", color: linkColor }}>HOME</Link>
                {paths.map((path, index) => (
                    <span key={index}>
                        {" > "}
                        <Link
                            to={path.breadcrumb?.hyperlink || "#"}
                            style={{ textDecoration: "none", color: linkColor }}
                        >
                            {path.breadcrumb?.displayLabel || path.pathText}
                        </Link>
                    </span>
                ))}
            </nav>
        );
    };

    return renderBreadcrumbs();
};

export default Breadcrumbs;
